import { Component, OnInit, OnDestroy } from '@angular/core';
import { ColDef, GridApi, GridReadyEvent, GridOptions, ApplyColumnStateParams } from 'ag-grid-community';
import { AgGridOptionsBuilder, AgGridFilterParams, AgGridColumns } from '../../../Compliance/AgGrid';
import { TaxDotComDocsSvcLogListAgGridDataSource } from './agGridDataSource';
import { WeissmanTimestampMsecFormatPipe } from '../../../UI-Lib/Pipes/Date-Format/date-formatting.pipe';
import { Subscription } from 'rxjs';
import { TaxDotComDocsSvcLogService } from '../taxDotComDocsSvcLog.service';
import { TaxDotComDocsSvcLogRepository } from '../taxDotComDocsSvcLog.repository';
import { TaxDotComDocsSvcLogListGridActionCellRendererComponent, TaxDotComDocsSvcLogListGridActionCellRendererParams } from './agGridActionCellRenderer.component';
import { WeissmanModalService } from '../../../Compliance/WeissmanModalService';
import { TaxDotComDocsSvcLogDetailsModalComponent, TaxDotComDocsSvcLogDetailsParams } from '../TaxDotCom-DocsSvc-Log-Details/taxDotComDocsSvcLogDetailsModal.component';
import { BusyIndicatorService } from '../../../Busy-Indicator';
import { RestrictService, Roles } from '../../../Common/Permissions/restrict.service';
import { UpgradeNavigationServiceHandler } from '../../../Common/Routing/upgrade-navigation-handler.service';

@Component({
    selector: 'taxdotcom-docs-svc-log-list',
    templateUrl: './taxDotComDocsSvcLogList.component.html',
    styleUrls: ['./taxDotComDocsSvcLogList.component.scss'],
    standalone: false
})
export class TaxDotComDocsSvcLogListComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _timestampPipe: WeissmanTimestampMsecFormatPipe,
        private readonly _taxDotComDocsSvcLogRepository: TaxDotComDocsSvcLogRepository,
        private readonly _taxDotComDocsSvcLogService: TaxDotComDocsSvcLogService,
        private readonly _modalService: WeissmanModalService,
        private readonly _busyService: BusyIndicatorService,
        private readonly _restrictService: RestrictService,
        private readonly _routerService: UpgradeNavigationServiceHandler) {
    }

    gridId: System.Guid = 'EDA88CE1-4997-4037-8A5E-1F1DCD18DC44';
    isInitialized: boolean = false;
    gridOptions: GridOptions = new AgGridOptionsBuilder()
        .withColumnPinning()
        .buildDefault(this);
    gridApi: GridApi;

    private _gridDataSource: TaxDotComDocsSvcLogListAgGridDataSource;
    private _refreshSub: Subscription;

    get refreshing(): boolean {
        return this._gridDataSource && this._gridDataSource.isRefreshing;
    }

    ngOnInit(): void {
        if (!(this._restrictService.isInRole(Roles.ADMINOPERATIONSVIEW) || this._restrictService.isInRole(Roles.ADMINOPERATIONSEDIT))) {
            this.navigateToUnauthorized();
            return;
        }

        this._refreshSub = this._taxDotComDocsSvcLogService.refresh$.subscribe(() => {
            this._refreshDataSource();
        });

        this.isInitialized = true;
    }

    ngOnDestroy(): void {
        this._refreshSub && this._refreshSub.unsubscribe();
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;

        const columns: ColDef[] = [
            {
                headerName: 'Summary ID',
                field: 'taxDotComDocsSvcSummaryLogId',
                width: AgGridColumns.textColumnSmallWidth
            },
            {
                headerName: 'Operaton Type',
                field: 'operationType',
                width: AgGridColumns.textColumnMedWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            },
            {
                headerName: 'Start Time',
                field: 'startTime',
                width: AgGridColumns.textColumnMedWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                valueFormatter: (params) => {
                    const model = params.data as Core.TaxDotComDocsSvcSummaryLogModel;
                    if (!(model && model.startTime)) {
                        return '';
                    }

                    return this._timestampPipe.transform(model.startTime);
                }
            },
            {
                headerName: 'End Time',
                field: 'endTime',
                width: AgGridColumns.textColumnMedWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                valueFormatter: (params) => {
                    const model = params.data as Core.TaxDotComDocsSvcSummaryLogModel;
                    if (!(model && model.endTime)) {
                        return '';
                    }

                    return this._timestampPipe.transform(model.endTime);
                }
            },
            {
                headerName: 'Tenant Id',
                field: 'tenantId',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            },
            {
                headerName: 'File Name',
                field: 'fileName',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            },
            {
                headerName: 'Request Payload',
                field: 'requestPayloadJson',
                width: AgGridColumns.textColumnWidth,
                maxWidth: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                sortable: false
            },
            {
                headerName: 'Response Payload',
                field: 'responsePayloadJson',
                width: AgGridColumns.textColumnWidth,
                maxWidth: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                sortable: false
            },
            {
                headerName: '',
                field: 'actions',
                pinned: 'right',
                width: AgGridColumns.getActionColumnWidth(1),
                minWidth: AgGridColumns.getActionColumnWidth(1),
                maxWidth: AgGridColumns.getActionColumnWidth(1),
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                suppressColumnsToolPanel: true,
                lockPinned: true,
                sortable: false,
                cellRenderer: TaxDotComDocsSvcLogListGridActionCellRendererComponent,
                cellRendererParams: {
                    viewDetails: this._viewDetails.bind(this)
                } as TaxDotComDocsSvcLogListGridActionCellRendererParams
            }
        ];

        const defaultSortModel: ApplyColumnStateParams = {
            state: [{ colId: 'taxDotComDocsSvcSummaryLogId', sort: 'desc' }],
            defaultState: { sort: null }
        };

        this.gridApi.setGridOption('paginationPageSize', 100);
        this.gridApi.setGridOption('columnDefs', columns);
        this.gridApi.applyColumnState(defaultSortModel);
        this._setDataSource();
    }

    async refresh(): Promise<void> {
        const busyRef = this._busyService.show({ message: 'Loading' });

        try {
            await this._taxDotComDocsSvcLogService.refresh();
        } finally {
            busyRef.hide();
        }
    }

    navigateToUnauthorized(): void {
        this._routerService.go('unauthorizedAccess', {});
    }

    private async _viewDetails(params: TaxDotComDocsSvcLogListGridActionCellRendererParams): Promise<void> {
        const taxDotComDocsSvcSummaryLog = params.data as Core.TaxDotComDocsSvcSummaryLogModel;
        if (!taxDotComDocsSvcSummaryLog) {
            return;
        }

        const detailParams: TaxDotComDocsSvcLogDetailsParams = {
            summaryLogId: taxDotComDocsSvcSummaryLog.taxDotComDocsSvcSummaryLogId
        };

        await this._modalService.showAsync(TaxDotComDocsSvcLogDetailsModalComponent, detailParams, 'modal-lg');
    }

    private _refreshDataSource(): void {
        if (!this._gridDataSource) {
            const success = this._setDataSource();
            if (!success) {
                return;
            }
        }

        this._gridDataSource.refresh();
    }

    private _setDataSource(): boolean {
        if (!this.gridApi || this._gridDataSource) {
            return;
        }

        this._gridDataSource = new TaxDotComDocsSvcLogListAgGridDataSource(this.gridApi, this._taxDotComDocsSvcLogRepository);
        this.gridApi.updateGridOptions({ datasource: this._gridDataSource });
        return true;
    }
}
